import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePrevious from '@rsos/utils/usePrevious';
import { trackCTMAcceptedCall } from '../components/CallTakerMode/ctmTracking';
import { isExtraInfoOrMeta } from '../components/JurisdictionView/helpers';
import useCallTakerModePerms from './useCallTakerModePerms';

const MS_PER_MINUTE = 60_000;

const toMinute = timestampMs => Math.floor(timestampMs / MS_PER_MINUTE);

/**
 * getMostRecentLoc, but comparing an ALI against a handset location at minute
 * precision when the ALI start time is exactly on the minute. Some GeoComm
 * feeds send ALI call start times rounded down to the minute, so a handset
 * location (ELS/iOS) from the same call always has the later start time and
 * the ALI never counts as the most recent location. A tie goes to the ALI, as
 * it does in getMostRecentLoc. ALI start times with seconds are compared
 * exactly, as before.
 * @param {Object} call callQueue[callerID]
 */
export const getMostRecentLocForCallTakerMode = call => {
  const locations = call?.core?.locations;
  const secondaryLocations = call?.core?.secondaryLocations;
  const primary = locations?.[locations.length - 1];
  const secondary = secondaryLocations?.[secondaryLocations.length - 1];

  if (
    !primary ||
    !secondary ||
    !('call_start_time' in primary) ||
    !secondary.application?.startsWith(ALI_UNDERSCORE) ||
    secondary.call_start_time % MS_PER_MINUTE !== 0
  ) {
    return getMostRecentLoc(call);
  }

  return toMinute(primary.call_start_time) > toMinute(secondary.call_start_time)
    ? primary
    : secondary;
};

const useCallTakerMode = () => {
  const dispatch = useDispatch();

  const { irpVersion } = useIsIRP();

  const { currentRole, profile } = useSelector(state => state.sinatra.user);

  const applicationName = currentRole?.application;

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  const prevNumberWithNewLocation = usePrevious(numberWithNewLocation);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasLocation = Boolean(
    filteredCalls?.[selectedCallerID]?.core?.locations?.length ||
      filteredCalls?.[selectedCallerID]?.core?.secondaryLocations?.length,
  );

  const activeIncidentID =
    selectedCallerID && hasLocation ? selectedCallerID : null;

  const mostRecentCall = filteredCalls?.[numberWithNewLocation];

  const mostRecentLocation =
    getMostRecentLocForCallTakerMode(mostRecentCall) || null;

  const mostRecentWorkstationPosition =
    (mostRecentCall?.workstationPositions?.length &&
      mostRecentCall.workstationPositions[
        mostRecentCall.workstationPositions.length - 1
      ]) ||
    null;

  const hasCallTakerModeAccess = useCallTakerModePerms();

  const isCallTakerModeOn = useMemo(() => {
    return profile?.attributes?.[applicationName]?.call_taker_mode_settings
      ?.is_enabled;
  }, [applicationName, profile]);

  const workstationPosition = useMemo(() => {
    return profile?.attributes?.[applicationName]?.call_taker_mode_settings
      ?.workstation_position;
  }, [applicationName, profile]);

  const autoSwitchToNextCall = useMemo(() => {
    return profile?.attributes?.[applicationName]?.call_taker_mode_settings
      ?.auto_switch_to_next_call;
  }, [applicationName, profile]);

  const shouldOpenCallPane = Boolean(
    hasCallTakerModeAccess &&
      isCallTakerModeOn &&
      mostRecentLocation?.application?.startsWith(ALI_UNDERSCORE) &&
      mostRecentWorkstationPosition === workstationPosition,
  );

  const mostRecentCallerID = mostRecentLocation?.caller_id;

  const handleCallTracking = useCallback(() => {
    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);

    trackCTMAcceptedCall(workstationPosition, irpVersion, payloadID, requestID);
  }, [irpVersion, mostRecentLocation, workstationPosition]);

  // Checks if call taker mode should open the call pane in all events view.
  useEffect(() => {
    if (!activeIncidentID && numberWithNewLocation && shouldOpenCallPane) {
      dispatch(setSelectedCallerID(mostRecentCallerID));

      handleCallTracking();
    }
  }, [
    activeIncidentID,
    mostRecentCallerID,
    dispatch,
    handleCallTracking,
    numberWithNewLocation,
    shouldOpenCallPane,
  ]);

  // Checks if call taker mode should auto switch to next call in detailed view.
  useEffect(() => {
    if (
      activeIncidentID &&
      autoSwitchToNextCall &&
      numberWithNewLocation !== prevNumberWithNewLocation &&
      shouldOpenCallPane
    ) {
      dispatch(setSelectedCallerID(mostRecentCallerID));

      handleCallTracking();
    }
  }, [
    activeIncidentID,
    autoSwitchToNextCall,
    dispatch,
    handleCallTracking,
    mostRecentCallerID,
    numberWithNewLocation,
    prevNumberWithNewLocation,
    shouldOpenCallPane,
  ]);

  return {
    mostRecentCallerID,
    shouldOpenCallPane,
  };
};

export default useCallTakerMode;
